import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import SectionHeader from '@/components/shared/SectionHeader';
import { ArrowRight, MapPin, CalendarDays } from 'lucide-react';

const NewsSection = ({ news = [] }) => {
    if (!news.length) return null;
    return (
        <section
            className="section-pad bg-[hsl(var(--secondary))]/50 relative overflow-hidden"
            data-testid="home-news-section"
        >
            <div className="absolute inset-0 grain-bg opacity-60 pointer-events-none" />
            <div className="container-page relative">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <SectionHeader
                        eyebrow="Latest News & Events"
                        title="What's happening at VVVSI."
                        description="Announcements, upcoming events, and recent milestones."
                    />
                </div>
                <div
                    className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid="news-events-grid"
                >
                    {news.slice(0, 6).map((n, i) => (
                        <motion.div
                            key={n.id}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            viewport={{ once: true, margin: '-60px' }}
                        >
                            <Card
                                className="h-full overflow-hidden bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)] card-lift"
                                data-testid="news-card"
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-muted">
                                    <img
                                        src={n.image_url}
                                        alt={n.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                    />
                                </div>
                                <CardContent className="p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge
                                            className={
                                                n.is_event
                                                    ? 'bg-[hsl(var(--accent))]/15 text-[hsl(30_90%_28%)] border-[hsl(var(--accent))]/30'
                                                    : 'bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20'
                                            }
                                        >
                                            {n.is_event ? 'Event' : n.category}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <CalendarDays className="h-3 w-3" /> {n.date}
                                        </span>
                                    </div>
                                    <div className="font-display font-semibold text-lg leading-snug">
                                        {n.title}
                                    </div>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                                        {n.excerpt}
                                    </p>
                                    {n.location && (
                                        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                                            <MapPin className="h-3 w-3" /> {n.location}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default NewsSection;
